import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, IdCard, Plus, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { Action, EmptyState, Field, PageHeader, Panel, PanelHeader, SearchField } from "./ui";
import { ConversationModal, ScheduleModal } from "./Conversations";
import { DEFAULT_SORT, SortSelect } from "./PatientSort";
import { AgeChip, cpfDigits, formatCpf, wardLabel, wardScope } from "@/lib/patient";
import { CareTeamFields } from "./StaffPicker";

const EMPTY_CARE_TEAM = { counselor_id: null, psychologist_id: null, psychiatrist_id: null };

export const TREATMENT_TYPES = [
  { value: "CHEMICAL_DEPENDENCE", label: "Dependência de Substâncias" },
  { value: "ALCOHOL_DEPENDENCE", label: "Dependência em Álcool" },
  { value: "BORDERLINE", label: "Borderline" },
  { value: "DEPRESSION", label: "Depressão" },
  { value: "ANXIETY", label: "Ansiedade / Síndrome do Pânico" },
  { value: "BIPOLAR", label: "Bipolaridade" },
  { value: "GAMBLING_PROBLEMS", label: "Problemas com Jogos" },
  { value: "BURNOUT", label: "Burnout" },
  { value: "OTHER", label: "Outros" },
];

export const PAYMENT_METHODS = [
  { value: "particular", label: "Particular" },
  { value: "convenio", label: "Convênio" },
  { value: "vaga_social", label: "Vaga social" },
  { value: "liminar", label: "Liminar" },
  { value: "undefined", label: "Não definido" },
];

export default function PortalPatients() {
  const { api, user } = useAuth();
  const canFilterMine = (user?.permissions || []).includes("my_patients_filter");
  const [patients, setPatients] = useState(null);
  const [query, setQuery] = useState("");
  const scope = wardScope(user);
  const [ward, setWard] = useState(scope.value);
  const [team, setTeam] = useState({ counselor: "", psychologist: "", psychiatrist: "" });
  const [conversationFor, setConversationFor] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [mine, setMine] = useState(false);
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [exporting, setExporting] = useState(false);
  const [wardOptions, setWardOptions] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    cpf: "",
    birth_date: "",
    admission_date: "",
    expected_discharge_date: "",
    guardian_name: "",
    treatment_type: "",
    treatment_type_other: "",
    payment_method: "undefined",
    health_plan_name: "",
  });
  const [careTeam, setCareTeam] = useState(EMPTY_CARE_TEAM);
  const [careOptions, setCareOptions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searching, setSearching] = useState(false);

  const resetForm = () => {
    setForm({
      full_name: "",
      cpf: "",
      birth_date: "",
      admission_date: "",
      expected_discharge_date: "",
      guardian_name: "",
      treatment_type: "",
      treatment_type_other: "",
      payment_method: "undefined",
      health_plan_name: "",
    });
    setCareTeam(EMPTY_CARE_TEAM);
  };

  const load = async (term = "", wardValue = "all", mineValue = false, statusValue = "active", teamValue = {}, sortValue = DEFAULT_SORT) => {
    try {
      const [{ data }, options] = await Promise.all([
        api.get("/portal/patients", {
          params: {
            q: term || undefined,
            ward: wardValue,
            mine: mineValue,
            status: statusValue,
            counselor: teamValue.counselor || undefined,
            psychologist: teamValue.psychologist || undefined,
            psychiatrist: teamValue.psychiatrist || undefined,
            sort: sortValue,
          },
        }),
        api.get("/portal/care-team/options"),
      ]);
      setPatients(data);
      setCareOptions(options.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPatients([]);
    }
  };

  useEffect(() => {
    api
      .get("/portal/patients/ward-options")
      .then(({ data }) => setWardOptions(data.items || []))
      .catch(() => setWardOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearching(true);
    const timer = setTimeout(async () => {
      await load(query.trim(), ward, mine, status, team, sort);
      setSearching(false);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, ward, mine, status, team, sort]);

  const exportList = async () => {
    setExporting(true);
    try {
      const response = await api.get("/portal/patients/export.xlsx", {
        params: {
          q: query.trim() || undefined,
          ward,
          mine,
          status,
          counselor: team.counselor || undefined,
          psychologist: team.psychologist || undefined,
          psychiatrist: team.psychiatrist || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "pacientes-erimus.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Lista exportada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setExporting(false);
  };

  const createPatient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/portal/patients", {
        ...form,
        health_plan_name: form.health_plan_name.trim(),
        ...careTeam,
      });
      toast.success(`Paciente disponível: ${data.full_name}`);
      resetForm();
      setCreating(false);
      await load(query.trim(), ward, mine, status, team);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const filtered = patients || [];

  return (
    <div data-testid="portal-patients-page" className="space-y-7">
      <PageHeader
        kicker="Casos acompanhados"
        title="Pacientes"
        subtitle="CPF sempre mascarado na interface. Abra um paciente para gerenciar episódio, vínculos de acompanhamento, boletins e medicamentos."
        actions={
          <>
            <Action
              variant="secondary"
              icon={Download}
              data-testid="patients-export-button"
              disabled={exporting}
              onClick={exportList}
            >
              {exporting ? "Gerando…" : "Exportar lista"}
            </Action>
            {creating ? null : (
              <Action
                icon={Plus}
                data-testid="patient-new-button"
                onClick={() => {
                  resetForm();
                  setCreating(true);
                }}
              >
                Novo paciente
              </Action>
            )}
          </>
        }
      />

      {creating ? (
        <Panel testId="patients-create-panel">
          <PanelHeader label="Cadastro" title="Novo paciente" />
          <form onSubmit={createPatient} className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Nome completo" htmlFor="full_name">
              <Input
                id="full_name"
                data-testid="patient-name-input"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="CPF" htmlFor="cpf" hint="Se o CPF já existir, o paciente é reaproveitado (operação idempotente).">
              <Input
                id="cpf"
                data-testid="patient-cpf-input"
                value={formatCpf(form.cpf)}
                inputMode="numeric"
                maxLength={14}
                placeholder="000.000.000-00"
                onChange={(e) => setForm({ ...form, cpf: cpfDigits(e.target.value) })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="Data de nascimento" htmlFor="birth_date">
              <Input
                id="birth_date"
                data-testid="patient-birth-input"
                type="date"
                value={form.birth_date}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
              />
            </Field>
            <Field label="Data de internação" htmlFor="admission_date" hint="Obrigatória para novos pacientes. Usada nos prazos da Linha do Tempo (fuso America/Sao_Paulo).">
              <Input
                id="admission_date"
                data-testid="patient-admission-input"
                type="date"
                value={form.admission_date}
                onChange={(e) => setForm({ ...form, admission_date: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="Nome do responsável" htmlFor="guardian_name" hint="Obrigatório para sair do estado 1 da Jornada da Rede de Apoio.">
              <Input
                id="guardian_name"
                data-testid="patient-guardian-input"
                value={form.guardian_name}
                onChange={(e) => setForm({ ...form, guardian_name: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
              />
            </Field>
            <Field label="Data prevista de alta" htmlFor="expected_discharge_date" hint="Opcional. Usada na visão de alocação da equipe nas Reuniões Clínicas.">
              <Input
                id="expected_discharge_date"
                data-testid="patient-expected-discharge-input"
                type="date"
                value={form.expected_discharge_date}
                onChange={(e) => setForm({ ...form, expected_discharge_date: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
              />
            </Field>
            <Field label="Tipo de tratamento" htmlFor="treatment_type">
              <select
                id="treatment_type"
                data-testid="patient-treatment-select"
                value={form.treatment_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    treatment_type: e.target.value,
                    treatment_type_other: e.target.value === "OTHER" ? form.treatment_type_other : "",
                  })
                }
                className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
              >
                <option value="">Não informado</option>
                {TREATMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            {form.treatment_type === "OTHER" ? (
              <Field label="Especifique o tipo de tratamento" htmlFor="treatment_type_other">
                <Input
                  id="treatment_type_other"
                  data-testid="patient-treatment-other-input"
                  value={form.treatment_type_other}
                  onChange={(e) => setForm({ ...form, treatment_type_other: e.target.value })}
                  className="h-11 rounded-xl text-[14.5px]"
                  required
                />
              </Field>
            ) : null}
            <Field label="Meio de pagamento" htmlFor="payment_method">
              <select
                id="payment_method"
                data-testid="patient-payment-method-select"
                value={form.payment_method}
                onChange={(e) =>
                  setForm({
                    ...form,
                    payment_method: e.target.value,
                    health_plan_name: ["convenio", "liminar"].includes(e.target.value) ? form.health_plan_name : "",
                  })
                }
                className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
            {["convenio", "liminar"].includes(form.payment_method) ? (
              <Field
                label="Plano de saúde"
                htmlFor="health_plan_name"
                hint={
                  form.payment_method === "liminar"
                    ? "Opcional para o meio de pagamento Liminar."
                    : "Obrigatório para o meio de pagamento Convênio."
                }
              >
                <Input
                  id="health_plan_name"
                  data-testid="patient-health-plan-input"
                  value={form.health_plan_name}
                  onChange={(e) => setForm({ ...form, health_plan_name: e.target.value })}
                  className="h-11 rounded-xl text-[14.5px]"
                  required={form.payment_method === "convenio"}
                />
              </Field>
            ) : null}
            <div data-testid="patients-care-team-section" className="space-y-4 rounded-2xl bg-sand/40 p-4">
              <div>
                <p className="er-label text-[12px] tracking-[0.16em]">Equipe responsável</p>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-600">
                  Escolha um profissional por cargo entre os funcionários ativos. Pode ser definido depois.
                </p>
              </div>
              <div className="[&>div]:sm:grid-cols-1">
                <CareTeamFields
                  options={careOptions}
                  values={careTeam}
                  onChange={setCareTeam}
                  testPrefix="patient-care-team"
                  stacked
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5 sm:col-span-2">
              <Action type="submit" icon={Plus} data-testid="patient-create-submit" disabled={saving}>
                {saving ? "Salvando…" : "Salvar paciente"}
              </Action>
              <Action
                type="button"
                variant="secondary"
                data-testid="patient-create-cancel"
                onClick={() => {
                  resetForm();
                  setCreating(false);
                }}
              >
                Cancelar
              </Action>
            </div>
          </form>
        </Panel>
      ) : null}

      <div className="grid gap-7">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchField
              testId="patients-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou CPF em toda a base"
              className="max-w-sm"
            />
            <select
              data-testid="patients-filter-status"
              aria-label="Situação"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 min-w-0 max-w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
            >
              <option value="active">Ativos</option>
              <option value="all">Todos</option>
              <option value="inactive">Inativos</option>
            </select>
            {scope.locked ? (
              <span
                data-testid="patients-ward-scope"
                className="inline-flex h-11 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
              >
                {scope.label}
              </span>
            ) : (
            <select
              data-testid="patients-filter-ward"
              aria-label="Ala"
              value={ward}
              onChange={(e) => setWard(e.target.value)}
              className="h-11 min-w-0 max-w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
            >
              {(wardOptions.length ? wardOptions : [{ value: "all", label: "Todas as alas" }]).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            )}
            {["counselor", "psychologist", "psychiatrist"].map((key) => (
              <select
                key={key}
                data-testid={`patients-filter-${key}`}
                aria-label={careOptions?.[key]?.label || key}
                value={team[key]}
                onChange={(e) => setTeam({ ...team, [key]: e.target.value })}
                className="h-11 min-w-0 max-w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
              >
                <option value="">{careOptions?.[key]?.label || key}: todos</option>
                <option value="none">Não definido</option>
                {(careOptions?.[key]?.items || []).map((s2) => (
                  <option key={s2.id} value={s2.id}>
                    {s2.name}
                  </option>
                ))}
              </select>
            ))}
            {canFilterMine ? (
              <label className="flex items-center gap-2 text-[14px] font-semibold text-ink-800">
                <input
                  type="checkbox"
                  data-testid="patients-filter-mine"
                  checked={mine}
                  onChange={(e) => setMine(e.target.checked)}
                />
                Meus pacientes
              </label>
            ) : null}
            <SortSelect value={sort} onChange={setSort} testId="patients-sort-select" />
            {query.trim() || (!scope.locked && ward !== "all") || mine || status !== "active" || team.counselor || team.psychologist || team.psychiatrist ? (
              <Action
                variant="secondary"
                data-testid="patients-filters-clear"
                onClick={() => {
                  setQuery("");
                  setWard(scope.value);
                  setMine(false);
                  setStatus("active");
                  setTeam({ counselor: "", psychologist: "", psychiatrist: "" });
                }}
              >
                Limpar filtros
              </Action>
            ) : null}
          </div>
          <p data-testid="patients-search-status" className="text-[13.5px] text-ink-500">
            {searching
              ? "Buscando…"
              : query.trim()
                ? `${filtered.length} resultado(s) para “${query.trim()}”`
                : `${filtered.length} paciente(s) mais recentes`}
            {ward !== "all"
              ? ` · Ala: ${
                  (wardOptions.find((o) => o.value === ward) || {}).label || ward
                }`
              : ""}
            {mine ? " · Meus pacientes" : ""}
            {status === "all" ? " · Ativos e inativos" : status === "inactive" ? " · Inativos" : ""}
          </p>

          {patients === null ? (
            <Loading label="Carregando pacientes…" />
          ) : filtered.length === 0 ? (
            <EmptyState
              testId="patients-empty-state"
              icon={UserRound}
              title={query.trim() ? "Nenhum paciente com esse nome ou CPF" : "Nenhum paciente ainda"}
              description={
                query.trim()
                  ? "Revise a busca ou limpe o campo para ver os pacientes mais recentes."
                  : "Use o botão “Novo paciente” para cadastrar o primeiro paciente e iniciar o acompanhamento."
              }
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map((p, i) => (
                <div key={p.id} className="flex flex-col">
                <Link
                  to={`/portal/pacientes/${p.id}`}
                  data-testid={`patient-card-${p.id}`}
                  className="er-rise rounded-2xl border border-border bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:border-sage-600/40 hover:bg-sage-50/40"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-start gap-3.5">
                    <Avatar
                      name={p.full_name}
                      photoUrl={p.photo_thumb_url}
                      size={44}
                      testId={`patient-avatar-${p.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-[17px] font-semibold leading-snug text-ink-900 break-words">
                        {p.full_name}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <AgeChip patient={p} testId={`patient-age-${p.id}`} />
                        <span data-testid={`patient-ward-${p.id}`} className="text-[13px] text-ink-500">
                          Ala: {wardLabel(p)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-2.5 flex items-center gap-2 text-[14.5px] text-ink-600">
                    <IdCard size={15} aria-hidden="true" /> CPF {p.cpf_masked}
                  </p>
                  <p className="mt-1.5 text-[13.5px] text-ink-500">
                    {p.admission_date ? (
                      `Internação em ${String(p.admission_date).slice(0, 10).split("-").reverse().join("/")}`
                    ) : (
                      <span data-testid={`patient-admission-pending-${p.id}`} className="font-semibold text-amber-700">
                        Data de internação pendente
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-[13.5px] text-ink-500">
                    <span data-testid={`patient-treatment-${p.id}`}>
                      Tipo de tratamento: {p.treatment_type_label}
                      {p.treatment_type_other ? ` · ${p.treatment_type_other}` : ""}
                    </span>
                  </p>
                  <p className="mt-1 text-[13.5px] text-ink-500">
                    <span data-testid={`patient-payment-${p.id}`}>
                      Meio de pagamento: {p.payment_method_label}
                      {p.health_plan_name ? ` · Plano: ${p.health_plan_name}` : ""}
                    </span>
                  </p>
                  <div data-testid={`patient-care-team-${p.id}`} className="mt-1.5 space-y-1">
                    {["counselor", "psychologist", "psychiatrist"].map((key) => (
                      <p key={key} className="text-[13.5px] text-ink-500">
                        {p.care_team_names?.[key]?.label || key}:{" "}
                        <span
                          data-testid={`patient-${key}-${p.id}`}
                          className={p.care_team_names?.[key]?.name ? "text-ink-700" : "font-semibold text-amber-700"}
                        >
                          {p.care_team_names?.[key]?.name || "Não definido"}
                        </span>
                      </p>
                    ))}
                  </div>
                  <p data-testid={`patient-last-conversation-${p.id}`} className="mt-1.5 text-[13.5px] text-ink-500">
                    {p.conversation?.last_conversation_date
                      ? `Última conversa: há ${p.conversation.days_since} dia(s)`
                      : "Nenhuma conversa registrada"}
                  </p>
                </Link>
                  {p.conversation?.can_write ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Action
                        size="sm"
                        variant="secondary"
                        data-testid={`patient-quick-conversation-${p.id}`}
                        onClick={() => setConversationFor(p)}
                      >
                        Registrar conversa
                      </Action>
                      <Action
                        size="sm"
                        variant="ghost"
                        data-testid={`patient-quick-schedule-${p.id}`}
                        onClick={() => setScheduleFor(p)}
                      >
                        Agendar conversa
                      </Action>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      {conversationFor ? (
        <ConversationModal
          patient={conversationFor}
          onClose={() => setConversationFor(null)}
          onSaved={() => load(query.trim(), ward, mine, status, team)}
        />
      ) : null}
      {scheduleFor ? (
        <ScheduleModal
          patient={scheduleFor}
          onClose={() => setScheduleFor(null)}
          onSaved={() => load(query.trim(), ward, mine, status, team)}
        />
      ) : null}
    </div>
  );
}
